import defaultLogo from '../assets/logo.png';
import defaultLogoBlue from '../assets/logo-blue.png';
import pmsGoLogo from '../assets/pmsgo-logo.png';
import { applyDynamicBranding } from './applyTheme';

export { applyDynamicBranding };

/**
 * Environment-based and Centralized Branding Configuration
 * 
 * Centralized properties:
 * - Application name (appName)
 * - Company name (companyName)
 * - Logo (logo, logoBlue)
 * - Favicon (favicon)
 * - Primary color (primaryColor)
 * - Secondary color (secondaryColor)
 * - Accent color (accentColor)
 * - Background color (backgroundColor)
 * - Text color (textColor)
 */

/**
 * Safe accessor for frontend environment variables, supporting both Vite (VITE_*)
 * and Create React App (REACT_APP_*) prefixes for global default branding.
 */
export const getEnvVariable = (key) => {
  if (typeof process !== 'undefined' && process.env) {
    if (process.env[`VITE_${key}`] !== undefined) return process.env[`VITE_${key}`];
    if (process.env[`REACT_APP_${key}`] !== undefined) return process.env[`REACT_APP_${key}`];

    if (key === 'BRAND_NAME' || key === 'APP_NAME') {
      return (
        process.env.VITE_APP_NAME ||
        process.env.REACT_APP_APP_NAME ||
        process.env.REACT_APP_NAME ||
        process.env.VITE_BRAND_NAME ||
        process.env.REACT_APP_BRAND_NAME
      );
    }
    if (key === 'LOGO_URL' || key === 'APP_LOGO') {
      return (
        process.env.VITE_APP_LOGO ||
        process.env.REACT_APP_APP_LOGO ||
        process.env.REACT_APP_LOGO ||
        process.env.VITE_LOGO_URL ||
        process.env.REACT_APP_LOGO_URL
      );
    }
    if (key === 'FAVICON_URL' || key === 'APP_FAVICON') {
      return (
        process.env.VITE_APP_FAVICON ||
        process.env.REACT_APP_APP_FAVICON ||
        process.env.REACT_APP_FAVICON ||
        process.env.VITE_FAVICON_URL ||
        process.env.REACT_APP_FAVICON_URL
      );
    }
  }
  return undefined;
};

export const isDevEnvironment = () => {
  // 1. Check runtime hostname if running in browser
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname || '';
    if (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.includes('admin-dev') ||
      host.includes('-dev') ||
      host.startsWith('dev.')
    ) {
      return true;
    }
  }

  // 2. Check explicit build-time brand name override if provided
  const brandOverride = getEnvVariable('BRAND_NAME');
  if (brandOverride) {
    return brandOverride.toUpperCase().includes('PMS');
  }

  // 3. Fallback to NODE_ENV
  return process.env.NODE_ENV === 'development';
};

/**
 * Adjust color brightness by percentage (-100 to +100)
 */
export const adjustColorBrightness = (hex, percent) => {
  if (!hex || typeof hex !== 'string') return hex;
  const cleanHex = hex.trim().replace('#', '');
  const num = parseInt(cleanHex.length === 3
    ? cleanHex.split('').map((c) => c + c).join('')
    : cleanHex, 16);
  if (isNaN(num)) return hex;

  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;

  return (
    '#' +
    (
      0x1000000 +
      (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 0 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

/**
 * Convert Hex color to RGB string or object
 */
export const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  let c = hex.trim().replace('#', '');
  if (c.length === 3) {
    c = c.split('').map((char) => char + char).join('');
  }
  if (c.length !== 6) return null;
  const num = parseInt(c, 16);
  if (isNaN(num)) return null;
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

/**
 * Retrieve global default branding based on environment variables and defaults.
 * Tenant-specific branding will dynamically override these properties.
 */
export const getDefaultBranding = () => {
  const isDev = isDevEnvironment();
  const defaultBrandName = isDev ? 'PMS GO' : 'Stekay';
  const envBrandName = getEnvVariable('BRAND_NAME');
  const appName = isDev
    ? (envBrandName && envBrandName.toUpperCase().includes('PMS') ? envBrandName : 'PMS GO')
    : (envBrandName || defaultBrandName);
  const companyName = getEnvVariable('COMPANY_NAME') || appName;
  const isPms = isDev || appName.toUpperCase().includes('PMS');

  const primaryColor = getEnvVariable('PRIMARY_COLOR') || '';
  const secondaryColor = getEnvVariable('SECONDARY_COLOR') || (primaryColor ? adjustColorBrightness(primaryColor, -15) : '');
  const accentColor = getEnvVariable('ACCENT_COLOR') || (primaryColor ? adjustColorBrightness(primaryColor, 35) : '');
  const backgroundColor = getEnvVariable('BG_COLOR') || '#ffffff';
  const textColor = getEnvVariable('TEXT_COLOR') || '#101828';
  const publicUrl = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  const pmsFavicon = `${publicUrl}/favicon.pms.ico`;
  const stekayFavicon = `${publicUrl}/favicon.ico`;

  const envLogo = getEnvVariable('APP_LOGO') || getEnvVariable('LOGO_URL');
  const logo = envLogo || null;
  const logoBlue = envLogo || null;
  const favicon = getEnvVariable('APP_FAVICON') || getEnvVariable('FAVICON_URL') || (isPms ? pmsFavicon : stekayFavicon);

  const prefix = isPms ? 'PMS ' : 'Ste';
  const suffix = isPms ? 'GO' : 'kay';
  const productLogo = isPms ? pmsGoLogo : defaultLogo;

  const rgb = primaryColor ? hexToRgb(primaryColor) : null;
  const primaryColorRgb = rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '';

  return {
    isDev,
    appName,
    companyName,
    productName: isPms ? 'PMS GO' : 'Stekay',
    productLogo,
    hotelLogo: null,
    hotelLogoUrl: null,
    prefix,
    suffix,
    tagline: 'ENTERPRISE SUITE',
    logo,
    logoUrl: logo,
    logoBlue,
    favicon,
    faviconUrl: favicon,
    primaryColor,
    secondaryColor,
    accentColor,
    backgroundColor,
    textColor,
    primaryColorRgb,
    portalTitle: `${appName} | Login - Property Management System`,
    portalDesc: `Sign in to ${appName} Property Management System. Secure portal for hotel staff and administrators to manage reservations, front desk operations, housekeeping, and guest folios.`,
    pageTitleSuffix: `${appName} | Property Management System`,
    showcaseAlt: `${appName} Dashboard Showcase`,
    logoAlt: appName,
  };
};

/**
 * Update DOM elements (CSS Custom Properties, Title, Favicon)
 */
export const updateBrandingDom = (branding) => {
  if (typeof document === 'undefined') return;

  const b = branding || getDefaultBranding();
  applyDynamicBranding(b);
  const root = document.documentElement;

  // 1. Inject CSS Variables
  if (b.primaryColor) {
    root.style.setProperty('--primary-color', b.primaryColor);
    const rgb = hexToRgb(b.primaryColor);
    if (rgb) {
      root.style.setProperty('--primary-color-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }
  }
  if (b.secondaryColor) {
    root.style.setProperty('--secondary-color', b.secondaryColor);
    root.style.setProperty('--primary-dark', b.secondaryColor);
  }
  if (b.accentColor) {
    root.style.setProperty('--accent-color', b.accentColor);
    root.style.setProperty('--primary-light', b.accentColor);
  }
  if (b.backgroundColor) {
    root.style.setProperty('--bg-default', b.backgroundColor);
  }
  if (b.textColor) {
    root.style.setProperty('--text-primary', b.textColor);
  }

  // 2. Update Favicon dynamically
  if (b.favicon) {
    const faviconSelectors = [
      'link[rel="icon"]',
      'link[rel="shortcut icon"]',
      'link[rel="apple-touch-icon"]'
    ];
    faviconSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => {
        el.setAttribute('href', b.favicon);
      });
    });
  }

  // 3. Update Document Title
  if (b.appName) {
    const currentTitle = document.title || '';
    if (currentTitle.includes('|')) {
      const parts = currentTitle.split('|');
      const pagePart = parts[0].trim();
      if (pagePart && !pagePart.toLowerCase().includes('property management system') && pagePart !== b.appName) {
        document.title = `${pagePart} | ${b.appName}`;
      } else {
        document.title = `${b.appName} | Property Management System`;
      }
    } else {
      document.title = `${b.appName} | Property Management System`;
    }
  }
};

/**
 * Backward compatibility getters
 */
export const getBrandConfig = () => {
  return getDefaultBranding();
};

export const BRAND_CONFIG = getBrandConfig();
export const BRAND_NAME = BRAND_CONFIG.appName;
